import { Injectable } from '@angular/core';
import { TemplateMapperConfig, EmailTemplate, EntityFieldSource, FieldSourceKind } from '../base-template-mapper';
import { SampleBasedTemplateMapper, FILTER_KEYS } from '../sample-based-template-mapper';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { TemplateContext, ReportTemplate } from '../../../models/reportTemplate.model';
import { Sample } from '../../../models/sample.model';
import { SampleSurvey } from '../../../models/sampleSurvey.model';
import { DateFormatService } from '../../dateFormat.service';
import { TranslateService } from '../../translate.service';
import { ENTITY_TYPES } from 'src/app/shared/widget-registry';
import { CustomField } from '../../../models/customField.model';
import { CustomFieldTypeEnum } from '../../../enums/customFieldType.enum';
import { MultiSampleTablePresentationService } from '../../../components/template-widgets/sample-widgets/multi-sample-table-widget/multi-sample-table-presentation.service';

export interface SampleSurveyData {
  survey: SampleSurvey;
  samples: Sample[];
}

@Injectable({ providedIn: 'root' })
export class SampleSurveyTemplateMapper extends SampleBasedTemplateMapper<SampleSurveyData> {
  readonly config: TemplateMapperConfig = {
    entityType: ENTITY_TYPES.SAMPLE_SURVEY,
    supportedFilters: [FILTER_KEYS.sampleNumber, FILTER_KEYS.sampleStatus, FILTER_KEYS.sampleCategory, FILTER_KEYS.agent, FILTER_KEYS.jurisdiction, FILTER_KEYS.oelType]
  };

  getEntityFieldSources(): EntityFieldSource[] {
    return [
      {
        key: 'survey',
        label: 'Survey',
        accent: 'var(--primary)',
        kind: FieldSourceKind.Inline,
        fields: [
          { path: 'survey.' + nameOf<SampleSurvey>(x => x.name), label: 'Survey Name' },
          { path: 'survey.' + nameOf<SampleSurvey>(x => x.surveyCode), label: 'Survey Code' },
          { path: 'survey.' + nameOf<SampleSurvey>(x => x.surveyDescription), label: 'Survey Description' },
          { path: 'survey.' + nameOf<SampleSurvey>(x => x.surveyPlannedStartDate), label: 'Start Date' },
          { path: 'survey.' + nameOf<SampleSurvey>(x => x.surveyPlannedEndDate), label: 'End Date' },
          { path: 'survey.sampledBy', label: 'Sampled By' },
        ],
      },
    ];
  }

  constructor(
    dateFormatService: DateFormatService,
    translateService: TranslateService,
    multiSampleTablePresentationService: MultiSampleTablePresentationService
  ) { super(dateFormatService, translateService, multiSampleTablePresentationService); }

  protected getSamples(data: SampleSurveyData): Sample[] {
    return data.samples;
  }

  async mapToTemplateContext(data: SampleSurveyData, template: ReportTemplate, userInputs: Record<string, string | boolean>, filteredData?: unknown[]): Promise<TemplateContext> {
    const { survey, samples } = data;
    const processedSamples = (filteredData as Sample[] | undefined) ?? samples;

    const sampledBy = Array.from(new Set(processedSamples.map(s => s.sampledByUsername).filter(Boolean))).sort().join(', ');

    const surveyData = this.createDynamicProxy(survey, {
      surveyPlannedStartDate: this.formatDate(survey.surveyPlannedStartDate),
      surveyPlannedEndDate: this.formatDate(survey.surveyPlannedEndDate),
      sampledBy
    });

    const multiSampleTable = this.buildMultiSampleTableContext(
      { survey: { name: survey.name, code: survey.surveyCode || '', description: survey.surveyDescription || '' }, samples: processedSamples },
      { selectedOelFilters: [], excludeAdjustedOel: false }
    );

    return {
      survey: surveyData,
      samples: processedSamples.map(sample => this.withResolvedSampleEnumLabels(sample)),
      computedData: { survey: surveyData, samples: processedSamples, multiSampleTable },
      userInputs: userInputs || this.getDefaultUserInputs()
    };
  }

  getDefaultUserInputs(): Record<string, string> {
    return { objective: '', notes: '' };
  }

  getEmailTemplate(data: SampleSurveyData, customFields?: CustomField[]): EmailTemplate {
    const { survey } = data;
    const endPoint = survey?.endPoint;
    const recipientEmail = endPoint ? this.getFieldValue(endPoint, CustomFieldTypeEnum.HrisEmailId, customFields) : '';
    const recipientId = endPoint?.id;

    return {
      subject: survey?.name || 'Survey Report',
      message: `Please find attached the results of the ${survey?.name || '[Survey Name]'} conducted from ${this.formatDate(survey?.surveyPlannedStartDate)} to ${this.formatDate(survey?.surveyPlannedEndDate)}.`,
      recipientEmail,
      recipientId,
      ccEmails: []
    };
  }
}
